import { matchPath, useLocation, useNavigate } from "react-router-dom";
import { Icon, IconType } from "../../../icons/Icon";
import {
  INavigationLink,
  useGetNavigationLinks,
} from "../../../infrastructure/navigation/useNavigationItems";
import styles from "./Navigation.module.scss";
import { RenderWhen } from "../../../infrastructure/components/RenderWhen";
import { useState } from "react";
import { classNames } from "primereact/utils";
import { NavigationItems } from "../../../infrastructure/routes/navigationItems";
import { routesMetadata } from "../../../infrastructure/routes/routesMetadata";
import { IRoute, routePaths } from "../../../infrastructure/routes/routePaths";

interface INavigationProps {
  hideSidebar: () => void;
}

export const Navigation = ({ hideSidebar }: INavigationProps) => {
  const items = useGetNavigationLinks();

  if (!items) {
    return;
  }

  return items.map((x, index) => (
    <NavigationItem
      item={x}
      key={getNavigationItemKey(index)}
      hideSidebar={hideSidebar}
    />
  ));
};

interface INavigationItemProps {
  item: INavigationLink;
  hideSidebar: () => void;
}

const NavigationItem = ({ item, hideSidebar }: INavigationItemProps) => {
  const location = useLocation();

  const matchingRoutes = routesMetadata.filter((x) =>
    matchPath(x.path, location.pathname)
  );

  const isItemActive = checkIsActive(matchingRoutes, item.navigationItem);

  const [isExpanded, setIsExpanded] = useState(
    hasActiveDescendant(matchingRoutes, item)
  );

  const navigate = useNavigate();

  const onItemClick = () => {
    if (item.href) {
      const isOnAppointmentPage = location.pathname === routePaths.portalAppointment;
      const isNavigatingToAppointment = item.href === routePaths.portalAppointment;

      if (isOnAppointmentPage && isNavigatingToAppointment) {
        navigate(0);
      } else {
        navigate(item.href);
      }

      hideSidebar();
      return;
    }

    setIsExpanded((prev) => !prev);
  };
  
  return (
    <div>
      <div>
        <div
          className={classNames(styles.item_parent, {
            [styles.item_parent_isActive]: isItemActive,
          })}
          onClick={onItemClick}
          {...(item.dataOnboarding ? { "data-onboarding": item.dataOnboarding } : {})}
        >
          <div className={styles.item_parent_left}>
            {/* A smaller icon (iconWidth) stays centred in the same slot, so every
                label starts at the same x. */}
            <span className={styles.item_parent_left_icon}>
              <Icon
                type={item.iconType}
                color={"#fff"}
                width={item.iconWidth ?? "34px"}
                height={item.iconHeight ?? "34px"}
              />
            </span>
            <div className={classNames(styles.item_parent_left_label)}>
              {item.label}
              <RenderWhen condition={!!item.tagLabel}>
                <span className={styles.item_parent_left_label_tag}>
                  {item.tagLabel}
                </span>
              </RenderWhen>
              <RenderWhen condition={!!item.hasUnreadDot}>
                <span className={styles.item_parent_left_label_dot_alert} />
              </RenderWhen>
            </div>
          </div>

          <RenderWhen
            condition={(item.children && item.children.length > 0) ?? false}
          >
            <div
              className={classNames(styles.item_parent_left_dropdown, {
                [styles.item_parent_left_dropdown_expanded]: isExpanded,
              })}
            >
              <Icon type={IconType.RightChevronIcon} color={"#fff"} />
            </div>
          </RenderWhen>
        </div>

        <RenderWhen
          condition={
            (isExpanded && item.children && item.children?.length > 0) ?? false
          }
        >
          <div className={styles.item_parent_left_children}>
            {item.children &&
              item.children.map((x, index) => (
                <NavigationItem
                  key={getNavigationItemKey(index)}
                  item={x}
                  hideSidebar={hideSidebar}
                />
              ))}
          </div>
        </RenderWhen>
      </div>
    </div>
  );
};

const getNavigationItemKey = (id: number) => {
  return `Navigation_item_${id}}`;
};

// Opens a dropdown for a page anywhere inside it, also in a nested group
// ("Администрация" → "Други" → "Зали"), so the page's own item is visible
const hasActiveDescendant = (
  matchingRoutes: IRoute[],
  item: INavigationLink
): boolean =>
  item.children?.some(
    (child) =>
      checkIsActive(matchingRoutes, child.navigationItem) ||
      hasActiveDescendant(matchingRoutes, child)
  ) ?? false;

const checkIsActive = (
  matchingRoutes?: IRoute[],
  currentLocationNavigationItem?: NavigationItems
) => {
  return (
    matchingRoutes &&
    currentLocationNavigationItem &&
    matchingRoutes.some(
      (x) => x.navigationItem === currentLocationNavigationItem
    )
  );
};
